import { useState, useEffect } from 'react'

interface ContextualHintProps {
  id: string
  icon?: string
  children: React.ReactNode
  showOnce?: boolean
}

const HINTS_KEY = 'reframer_dismissed_hints'

function getDismissedHints(): string[] {
  try {
    return JSON.parse(localStorage.getItem(HINTS_KEY) || '[]')
  } catch {
    return []
  }
}

function dismissHint(id: string) {
  const dismissed = getDismissedHints()
  if (!dismissed.includes(id)) {
    dismissed.push(id)
    localStorage.setItem(HINTS_KEY, JSON.stringify(dismissed))
  }
}

export function ContextualHint({ id, icon = '💡', children, showOnce = true }: ContextualHintProps) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (showOnce) {
      const dismissed = getDismissedHints()
      setVisible(!dismissed.includes(id))
    } else {
      setVisible(true)
    }
  }, [id, showOnce])

  const handleDismiss = () => {
    if (showOnce) {
      dismissHint(id)
    }
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div className="hint-banner">
      <span className="hint-icon">{icon}</span>
      <div className="hint-content">
        <div className="hint-text">{children}</div>
        <button className="hint-dismiss" onClick={handleDismiss}>
          Got it
        </button>
      </div>
    </div>
  )
}

// Pre-defined hints for different contexts
export const HINTS = {
  NEW_SESSION_VOICE: {
    id: 'new-session-voice',
    icon: '🎙️',
    content: 'Speak naturally about what\'s bothering you. The more detail you share, the better we can help you reframe.',
  },
  NEW_SESSION_TEXT: {
    id: 'new-session-text',
    icon: '✍️',
    content: 'Write freely about the situation and your thoughts. Don\'t worry about grammar—just get your feelings out.',
  },
  REVIEW_DISTORTIONS: {
    id: 'review-distortions',
    icon: '🧠',
    content: 'Cognitive distortions are common thinking patterns that can distort reality. Tap any that feel familiar.',
  },
  HISTORY_PATTERNS: {
    id: 'history-patterns',
    icon: '📊',
    content: 'Look for patterns in your sessions. Do certain situations trigger similar thoughts?',
  },
  INSIGHTS_FIRST: {
    id: 'insights-first',
    icon: '🔮',
    content: 'After a few sessions, we\'ll identify recurring themes in your thinking to help you understand yourself better.',
  },
}
